//dependencies
import { FloatingPortal, autoPlacement, autoUpdate, offset, useFloating } from "@floating-ui/react";
import { animated, useSpring } from "@react-spring/web";
import React, { useEffect, useRef, useState } from "react";

//api
import { useCompanySettings, useGoalDetails } from "../../../../../api/hooks";

//utils
import { getGoalIdFromLink } from "../utils";

//components
import GoalCard from "..";

//This component is set in app.tsx and is a wrapper for the GoalCard component
//This component enables hovering on a link to a goal-details oage to show a tooltip with the goal details
// you can make the tooltip not appear by setting the class no-goal-tooltip on the link or passing the property hideInternalCard to the Link component

const GoalTooltipCardWrapper = () => {
	const [goalId, setGoalId] = useState<string | null>(null);
	const [isTornadoTemplate, setIsTornadoTemplate] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const visibilityTimeout = useRef<NodeJS.Timeout | null>(null);
	const [linkRef, setLinkRef] = useState<Element | null>(null);
	const cardRef = useRef<HTMLDivElement | null>(null);
	const currentRoute = window.location.pathname;

	const { data, isLoading } = useGoalDetails({ goalID: goalId || undefined, disableDefaultErrorHandling: true });
	const { goal } = data || {};
	const { data: companySettings, isLoading: isCompanySettingsLoading } = useCompanySettings();
	const goalWeightEnabled = companySettings?.functions?.goals?.attributes["goalWeights"] || false;

	const [springs, api] = useSpring(
		() => ({
			from: { opacity: 0, marginTop: -4 },
			to: { opacity: 1, marginTop: 0 },
		}),
		[]
	);

	useEffect(() => {
		setGoalId(null);
		setIsHovered(false);
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef?.current);
		}
	}, [currentRoute]);

	useEffect(() => {
		let timeout: NodeJS.Timeout;
		if (goal?._id) {
			if (isHovered) {
				api.start({ opacity: 1, config: { duration: 50 } });
			} else {
				api.start({ opacity: 0, marginTop: -4, config: { duration: 0 } });
				timeout = setTimeout(() => {
					setGoalId(null);
				}, 200);
			}
		}

		return () => clearTimeout(timeout);
	}, [goal?._id, isHovered]);

	useEffect(() => {
		// if the link is iwithin id of react-app, set isTornadoTemplate to true
		const reactAppRef = document.getElementById("react-app");
		if (!reactAppRef || !reactAppRef.contains(linkRef)) {
			setIsTornadoTemplate(true);
		} else {
			setIsTornadoTemplate(false);
		}

		//These are jquery listeners to set the hover state of the component
		$(document).on("mouseenter", "[href^='/goal/']", () => {
			// get hovered element with href that triggered this function
			const hoveredElement = document.querySelector("[href^='/goal/']:hover");
			// if hovered element is not null and does not have id goal-card, and does not have classname no-tooltip
			//set linkRef to hovered element
			if (
				hoveredElement &&
				!hoveredElement.id.includes("goal-card") &&
				!hoveredElement.classList.contains("no-tooltip")
			) {
				// get href of hovered element
				const href = hoveredElement.getAttribute("href");
				if (href) {
					const goalIdFromLink = getGoalIdFromLink(href);
					if (hoverTimeoutRef.current) {
						clearTimeout(hoverTimeoutRef.current);
					}
					if (visibilityTimeout.current) {
						clearTimeout(visibilityTimeout.current);
					}
					hoverTimeoutRef.current = setTimeout(() => {
						setGoalId(goalIdFromLink);
						setLinkRef(hoveredElement);
						setIsHovered(true);
					}, 400);
				}
			}
		});

		$(document).on("mouseleave", "[href^='/goal/']", () => {
			// if the cardRef is hovered or goal card is hovered, do nothing
			if (
				cardRef.current?.contains(document.querySelector(":hover")) ||
				document.querySelector("#goal-card:hover")
			) {
				return;
			}
			visibilityTimeout.current = setTimeout(() => {
				setIsHovered(false);
			}, 400);
		});

		// if we enter the goal card, clear the kill timer
		$(document).on("mouseenter", "#goal-card", () => {
			setIsHovered(true);
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
			if (visibilityTimeout.current) {
				clearTimeout(visibilityTimeout.current);
			}
		});

		// if we leave the goal card, instantiate the kill timer
		$(document).on("mouseleave", "#goal-card", () => {
			setIsHovered(false);
		});

		return () => {
			$(document).off("mouseenter", "[href^='/goal/']");
			$(document).off("mouseleave", "[href^='/goal/']");
			$(document).off("mouseenter", "#goal-card");
			$(document).off("mouseleave", "#goal-card");
		};
	}, [goalId, isHovered, linkRef]);

	const { refs, floatingStyles } = useFloating({
		placement: "bottom-start",
		strategy: "absolute",
		elements: {
			reference: linkRef,
		},
		middleware: [
			offset(8),
			autoPlacement({
				crossAxis: true,
				allowedPlacements: isTornadoTemplate ? undefined : ["bottom-start", "bottom-end"],
				padding: 4,
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	if (!goal || !goalId || isLoading || isCompanySettingsLoading) {
		return null;
	}
	return (
		<FloatingPortal id="curtain-scroll-container">
			<animated.div ref={refs.setFloating} style={{ ...floatingStyles, ...springs, zIndex: 100000 }}>
				<GoalCard goal={{ ...goal, goal_weight: goalWeightEnabled ? goal?.goal_weight : undefined }} />
			</animated.div>
		</FloatingPortal>
	);
};

export default GoalTooltipCardWrapper;
